/**
 * Employer Journey Dashboard
 *
 * Landing page for employer organizations that provides:
 * - Visual 5-step journey progress tracker
 * - Key metrics and ROI summary
 * - Quick actions and recommendations
 * - Navigation to specific capabilities
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  TrendingUp,
  DollarSign,
  CheckCircle2,
  Circle,
  ChevronRight,
  ArrowRight,
  AlertTriangle,
  Bell,
  BarChart3,
  Settings,
  Link2,
  Brain,
  FileCheck,
  HeartHandshake,
  Target,
  Sparkles,
  RefreshCw,
  Building2,
  Clock,
  Activity,
  Shield,
  Play,
  Gift,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

interface JourneyStep {
  id: string;
  number: number;
  title: string;
  shortTitle: string;
  description: string;
  icon: React.ElementType;
  status: 'completed' | 'in_progress' | 'pending';
  navTarget: string;
  navSubTab?: string;
  metrics?: { label: string; value: string | number }[];
}

interface DashboardMetrics {
  totalEmployees: number;
  atRiskEmployees: number;
  estimatedROI: number;
  estimatedSavings: number;
  activePrograms: number;
  engagementRate: number;
  pendingConsents: number;
  activeWorkflows: number;
}

interface RecommendedAction {
  id: string;
  type: 'warning' | 'info' | 'success' | 'action';
  title: string;
  description: string;
  actionLabel: string;
  navTarget: string;
  navSubTab?: string;
  priority: number;
}

export default function EmployerJourneyDashboard() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalEmployees: 0,
    atRiskEmployees: 0,
    estimatedROI: 0,
    estimatedSavings: 0,
    activePrograms: 0,
    engagementRate: 0,
    pendingConsents: 0,
    activeWorkflows: 0,
  });
  const [setupProgress, setSetupProgress] = useState({
    hrisConnected: false,
    employeesImported: false,
    programsConfigured: false,
    consentsInitiated: false,
    analyticsReviewed: false,
  });

  // Fetch dashboard data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch employer dashboard metrics
        const employerId = localStorage.getItem('active_employer_id') || localStorage.getItem('employer_id');
        if (!employerId) {
          setLoading(false);
          return;
        }

        // Fetch retention data for metrics
        const retentionUrl = getWeaveinApiUrl(`/api/employer/${employerId}/retention-risk`);
        const retentionResponse = await authFetch(retentionUrl);

        if (retentionResponse.ok) {
          const data = await retentionResponse.json();
          setMetrics(prev => ({
            ...prev,
            totalEmployees: data.total_employees || 0,
            atRiskEmployees: data.high_risk_count || 0,
            estimatedROI: data.potential_savings || 0,
            estimatedSavings: data.estimated_annual_savings || 0,
            engagementRate: data.engagement_rate || 0,
          }));

          // Update setup progress based on data
          setSetupProgress(prev => ({
            ...prev,
            employeesImported: (data.total_employees || 0) > 0,
            analyticsReviewed: (data.total_employees || 0) > 0,
          }));
        }

        // Fetch program count
        const programsUrl = getWeaveinApiUrl(`/api/employer-benefit-programs/employer/${employerId}`);
        const programsResponse = await authFetch(programsUrl);

        if (programsResponse.ok) {
          const programs = await programsResponse.json();
          setMetrics(prev => ({
            ...prev,
            activePrograms: Array.isArray(programs) ? programs.length : 0,
          }));
          setSetupProgress(prev => ({
            ...prev,
            programsConfigured: Array.isArray(programs) && programs.length > 0,
          }));
        }

        // Check HRIS integration status
        const integrationsUrl = getWeaveinApiUrl('/api/integrations/status');
        const integrationsResponse = await authFetch(integrationsUrl);

        if (integrationsResponse.ok) {
          const integrations = await integrationsResponse.json();
          const hasHris = integrations.some?.((i: any) =>
            i.type === 'hris' && i.status === 'active'
          );
          setSetupProgress(prev => ({
            ...prev,
            hrisConnected: hasHris,
          }));
        }

        // Fetch intervention recommendations for at-risk count and workflows
        const interventionsUrl = getWeaveinApiUrl(`/api/employer/${employerId}/intervention-recommendations`);
        const interventionsResponse = await authFetch(interventionsUrl);

        if (interventionsResponse.ok) {
          const interventionsData = await interventionsResponse.json();
          const recommendations = interventionsData.recommendations || [];
          const highRiskCount = recommendations.filter((r: any) => r.risk_level === 'high').length;
          const activeWorkflowsCount = recommendations.filter((r: any) =>
            r.status === 'in_progress' || r.has_active_referral
          ).length;

          setMetrics(prev => ({
            ...prev,
            atRiskEmployees: highRiskCount || prev.atRiskEmployees,
            activeWorkflows: activeWorkflowsCount,
            pendingConsents: recommendations.filter((r: any) => r.consent_pending).length,
          }));
        }

        // Fetch service referrals for workflow tracking
        const referralsUrl = getWeaveinApiUrl(`/api/service-referrals?employer_id=${employerId}&status=pending,in_progress`);
        const referralsResponse = await authFetch(referralsUrl);

        if (referralsResponse.ok) {
          const referralsData = await referralsResponse.json();
          const activeReferrals = Array.isArray(referralsData) ? referralsData.length :
            (referralsData.referrals?.length || 0);

          setMetrics(prev => ({
            ...prev,
            activeWorkflows: Math.max(prev.activeWorkflows, activeReferrals),
          }));

          setSetupProgress(prev => ({
            ...prev,
            consentsInitiated: activeReferrals > 0,
          }));
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  // Calculate journey steps based on setup progress
  const journeySteps: JourneyStep[] = useMemo(() => [
    {
      id: 'connect',
      number: 1,
      title: 'Connect & Configure',
      shortTitle: 'Connect',
      description: 'Integrate HRIS and configure benefit plans',
      icon: Link2,
      status: setupProgress.hrisConnected && setupProgress.employeesImported
        ? 'completed'
        : setupProgress.hrisConnected || setupProgress.employeesImported
          ? 'in_progress'
          : 'pending',
      navTarget: 'integrations',
      metrics: [
        { label: 'Employees', value: metrics.totalEmployees },
        { label: 'Programs', value: metrics.activePrograms },
      ],
    },
    {
      id: 'analyze',
      number: 2,
      title: 'Analyze & Predict',
      shortTitle: 'Analyze',
      description: 'ML-powered risk analysis and retention predictions',
      icon: Brain,
      status: setupProgress.analyticsReviewed && metrics.totalEmployees > 0
        ? 'completed'
        : metrics.totalEmployees > 0
          ? 'in_progress'
          : 'pending',
      navTarget: 'employee-retention',
      navSubTab: 'interventions',
      metrics: [
        { label: 'At-Risk', value: metrics.atRiskEmployees },
        { label: 'Analyzed', value: metrics.totalEmployees },
      ],
    },
    {
      id: 'offer',
      number: 3,
      title: 'Offer & Engage',
      shortTitle: 'Offer',
      description: 'Extend program offers and collect consent',
      icon: FileCheck,
      status: setupProgress.consentsInitiated
        ? 'completed'
        : setupProgress.programsConfigured
          ? 'in_progress'
          : 'pending',
      navTarget: 'program-consent-management',
      metrics: [
        { label: 'Pending', value: metrics.pendingConsents },
        { label: 'Engaged', value: `${metrics.engagementRate}%` },
      ],
    },
    {
      id: 'support',
      number: 4,
      title: 'Support & Refer',
      shortTitle: 'Support',
      description: 'Case management and care referrals',
      icon: HeartHandshake,
      status: metrics.activeWorkflows > 0 ? 'in_progress' : 'pending',
      navTarget: 'employer-workflow-orchestration',
      metrics: [
        { label: 'Active', value: metrics.activeWorkflows },
        { label: 'Workflows', value: 'View' },
      ],
    },
    {
      id: 'measure',
      number: 5,
      title: 'Measure ROI',
      shortTitle: 'Measure',
      description: 'Track outcomes and demonstrate value',
      icon: Target,
      status: metrics.estimatedROI > 0 ? 'in_progress' : 'pending',
      navTarget: 'employee-retention',
      metrics: [
        { label: 'Est. ROI', value: `$${(metrics.estimatedROI / 1000).toFixed(0)}K` },
        { label: 'Savings', value: `$${(metrics.estimatedSavings / 1000).toFixed(0)}K` },
      ],
    },
  ], [setupProgress, metrics]);

  // Calculate overall progress
  const overallProgress = useMemo(() => {
    const completed = journeySteps.filter(s => s.status === 'completed').length;
    const inProgress = journeySteps.filter(s => s.status === 'in_progress').length;
    return Math.round(((completed + inProgress * 0.5) / journeySteps.length) * 100);
  }, [journeySteps]);

  // Generate recommended actions
  const recommendedActions: RecommendedAction[] = useMemo(() => {
    const actions: RecommendedAction[] = [];

    if (!setupProgress.hrisConnected) {
      actions.push({
        id: 'connect-hris',
        type: 'action',
        title: 'Connect your HRIS system',
        description: 'Import employee data automatically from your HR system',
        actionLabel: 'Setup Integration',
        navTarget: 'integrations',
        priority: 1,
      });
    }

    if (metrics.atRiskEmployees > 0) {
      actions.push({
        id: 'review-risk',
        type: 'warning',
        title: `${metrics.atRiskEmployees} employees at elevated risk`,
        description: 'Review ML predictions and create action plans',
        actionLabel: 'View Analysis',
        navTarget: 'employee-retention',
        navSubTab: 'interventions',
        priority: 2,
      });
    }

    if (metrics.pendingConsents > 0) {
      actions.push({
        id: 'pending-consents',
        type: 'info',
        title: `${metrics.pendingConsents} consent requests pending`,
        description: 'Follow up with employees who haven\'t responded',
        actionLabel: 'Manage Consents',
        navTarget: 'program-consent-management',
        priority: 3,
      });
    }

    if (!setupProgress.programsConfigured) {
      actions.push({
        id: 'configure-programs',
        type: 'action',
        title: 'Configure benefit programs',
        description: 'Set up programs to offer to your employees',
        actionLabel: 'Configure Programs',
        navTarget: 'programs',
        priority: 4,
      });
    }

    if (metrics.totalEmployees > 0 && metrics.engagementRate < 50) {
      actions.push({
        id: 'improve-engagement',
        type: 'info',
        title: 'Engagement opportunity',
        description: `Current engagement rate is ${metrics.engagementRate}%. Consider targeted outreach.`,
        actionLabel: 'View Recommendations',
        navTarget: 'employee-retention',
        navSubTab: 'interventions',
        priority: 5,
      });
    }

    return actions.sort((a, b) => a.priority - b.priority).slice(0, 4);
  }, [setupProgress, metrics]);

  const getStepStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-6 h-6 text-green-600" />;
      case 'in_progress':
        return <Activity className="w-6 h-6 text-blue-600 animate-pulse" />;
      default:
        return <Circle className="w-6 h-6 text-gray-300" />;
    }
  };

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'action':
        return <Sparkles className="w-5 h-5 text-purple-500" />;
      default:
        return <Bell className="w-5 h-5 text-blue-500" />;
    }
  };

  const handleNavigate = (target: string, subTab?: string) => {
    // Use the onTabChange pattern from App.tsx
    const event = new CustomEvent('changeTab', { detail: target });
    window.dispatchEvent(event);

    // If a sub-tab is specified, store it for the target component to read
    if (subTab) {
      localStorage.setItem(`${target}_defaultTab`, subTab);
      // Also dispatch a custom event for immediate tab switching
      setTimeout(() => {
        const subTabEvent = new CustomEvent('setSubTab', { detail: { component: target, tab: subTab } });
        window.dispatchEvent(subTabEvent);
      }, 100);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your benefits dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl">
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                Employer Benefits Hub
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Your journey to employee retention and ROI
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.reload()}
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => handleNavigate('employee-retention')}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
            >
              <BarChart3 className="w-4 h-4" />
              Full Analytics
            </Button>
          </div>
        </div>
      </div>

      {/* Journey Progress Section */}
      <Card className="mb-6 border-purple-200 dark:border-purple-800">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-600" />
                Your Benefits Journey
              </CardTitle>
              <CardDescription>
                Complete each step to maximize ROI and employee retention
              </CardDescription>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-purple-600">{overallProgress}%</div>
              <div className="text-xs text-gray-500">Complete</div>
            </div>
          </div>
          <Progress value={overallProgress} className="h-2 mt-2" />
        </CardHeader>
        <CardContent>
          {/* Journey Steps */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 mt-4">
            {journeySteps.map((step, index) => {
              const Icon = step.icon;
              const isActive = step.status === 'in_progress';
              const isComplete = step.status === 'completed';

              return (
                <div
                  key={step.id}
                  onClick={() => handleNavigate(step.navTarget, step.navSubTab)}
                  className={`
                    relative p-4 rounded-xl border-2 cursor-pointer transition-all
                    ${isComplete
                      ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                      : isActive
                        ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/20 dark:border-blue-700 ring-2 ring-blue-200'
                        : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {/* Step number badge */}
                  <div className={`
                    absolute -top-2 -left-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                    ${isComplete
                      ? 'bg-green-600 text-white'
                      : isActive
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                    }
                  `}>
                    {step.number}
                  </div>

                  {/* Connector line */}
                  {index < journeySteps.length - 1 && (
                    <div className="hidden sm:block absolute top-1/2 -right-2 w-4 h-0.5 bg-gray-300 dark:bg-gray-600" />
                  )}

                  <div className="flex flex-col items-center text-center">
                    <div className={`
                      p-2 rounded-lg mb-2
                      ${isComplete
                        ? 'bg-green-100 dark:bg-green-900/40'
                        : isActive
                          ? 'bg-blue-100 dark:bg-blue-900/40'
                          : 'bg-gray-100 dark:bg-gray-700'
                      }
                    `}>
                      <Icon className={`
                        w-6 h-6
                        ${isComplete
                          ? 'text-green-600'
                          : isActive
                            ? 'text-blue-600'
                            : 'text-gray-400'
                        }
                      `} />
                    </div>
                    <h3 className={`
                      font-semibold text-sm mb-1
                      ${isComplete || isActive ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500'}
                    `}>
                      {step.shortTitle}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 hidden lg:block">
                      {step.description}
                    </p>

                    {/* Step metrics */}
                    {step.metrics && (
                      <div className="flex gap-2 mt-2">
                        {step.metrics.map((metric, i) => (
                          <div key={i} className="text-xs">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">
                              {metric.value}
                            </span>
                            <span className="text-gray-400 ml-1">{metric.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Status indicator */}
                  <div className="absolute -top-2 -right-2">
                    {getStepStatusIcon(step.status)}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => handleNavigate('employee-retention')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-blue-500" />
              <span className="text-sm text-muted-foreground">Total Employees</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {metrics.totalEmployees.toLocaleString()}
            </div>
            <p className="text-xs text-gray-500 mt-1">Imported and analyzed</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => handleNavigate('employee-retention')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <span className="text-sm text-muted-foreground">At-Risk Employees</span>
            </div>
            <div className="text-3xl font-bold text-amber-600">
              {metrics.atRiskEmployees}
            </div>
            <p className="text-xs text-gray-500 mt-1">Need intervention</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => handleNavigate('employee-retention')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              <span className="text-sm text-muted-foreground">Estimated ROI</span>
            </div>
            <div className="text-3xl font-bold text-green-600">
              ${(metrics.estimatedROI / 1000).toFixed(0)}K
            </div>
            <p className="text-xs text-gray-500 mt-1">Potential annual savings</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => handleNavigate('programs')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Gift className="h-5 w-5 text-purple-500" />
              <span className="text-sm text-muted-foreground">Active Programs</span>
            </div>
            <div className="text-3xl font-bold text-purple-600">
              {metrics.activePrograms}
            </div>
            <p className="text-xs text-gray-500 mt-1">Configured benefits</p>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recommended Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Recommended Actions
            </CardTitle>
            <CardDescription>
              Priority tasks to maximize your benefits ROI
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {recommendedActions.length > 0 ? (
              recommendedActions.map((action) => (
                <div
                  key={action.id}
                  className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  onClick={() => handleNavigate(action.navTarget, action.navSubTab)}
                >
                  {getActionIcon(action.type)}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                      {action.title}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {action.description}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" className="flex-shrink-0">
                    {action.actionLabel}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>All caught up! No pending actions.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Navigation */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-blue-600" />
              Quick Navigation
            </CardTitle>
            <CardDescription>
              Jump to key capabilities
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-purple-50 hover:border-purple-300"
                onClick={() => handleNavigate('employee-retention')}
              >
                <BarChart3 className="w-6 h-6 text-purple-600" />
                <span className="text-sm font-medium">Benefits Analytics</span>
              </Button>

              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-300"
                onClick={() => handleNavigate('program-consent-management')}
              >
                <Shield className="w-6 h-6 text-blue-600" />
                <span className="text-sm font-medium">Program Consent</span>
              </Button>

              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-green-50 hover:border-green-300"
                onClick={() => handleNavigate('employer-workflow-orchestration')}
              >
                <Play className="w-6 h-6 text-green-600" />
                <span className="text-sm font-medium">Workflows</span>
              </Button>

              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-amber-50 hover:border-amber-300"
                onClick={() => handleNavigate('integrations')}
              >
                <Link2 className="w-6 h-6 text-amber-600" />
                <span className="text-sm font-medium">Integrations</span>
              </Button>

              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-pink-50 hover:border-pink-300"
                onClick={() => handleNavigate('programs')}
              >
                <Gift className="w-6 h-6 text-pink-600" />
                <span className="text-sm font-medium">Programs</span>
              </Button>

              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-cyan-50 hover:border-cyan-300"
                onClick={() => handleNavigate('members')}
              >
                <Users className="w-6 h-6 text-cyan-600" />
                <span className="text-sm font-medium">Employees</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Coming Soon Section */}
      <Card className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border-purple-200 dark:border-purple-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Coming Soon
          </CardTitle>
          <CardDescription>
            Features in development to enhance your experience
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-white/60 dark:bg-gray-800/60">
              <Settings className="w-5 h-5 text-purple-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-sm">Onboarding Wizard</h4>
                <p className="text-xs text-gray-500">Guided first-time setup experience</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-white/60 dark:bg-gray-800/60">
              <Users className="w-5 h-5 text-cyan-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-sm">Employee Self-Service</h4>
                <p className="text-xs text-gray-500">Enhanced member portal</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
